/**
 * Fully synthesized audio: no sample files, no licensing questions, tiny
 * download. SFX are built from oscillators and filtered noise; the music is a
 * generative loop whose intensity follows the match.
 */
export type SfxName =
  | 'click'
  | 'summon'
  | 'merge'
  | 'lane'
  | 'shot'
  | 'fireball'
  | 'mortar'
  | 'laser'
  | 'hit'
  | 'explosion'
  | 'death'
  | 'leak'
  | 'leakEnemy'
  | 'boss'
  | 'commander'
  | 'shop'
  | 'heal'
  | 'error'
  | 'win'
  | 'lose'
  | 'overtime'
  | 'pickup'
  | 'drop';

const MIN_INTERVAL: Partial<Record<SfxName, number>> = {
  shot: 0.06,
  hit: 0.05,
  fireball: 0.12,
  laser: 0.25,
  explosion: 0.08,
  death: 0.07,
  heal: 0.3,
  mortar: 0.15,
};

export class AudioEngine {
  private ctx: AudioContext | null = null;
  private master: GainNode | null = null;
  private sfxBus: GainNode | null = null;
  private musicBus: GainNode | null = null;
  private noiseBuffer: AudioBuffer | null = null;
  private lastPlayed: Partial<Record<SfxName, number>> = {};
  private musicTimer: number | null = null;
  private nextBarTime = 0;
  private bar = 0;
  sfxEnabled = true;
  musicEnabled = true;
  intensity = 0;
  private musicPlaying = false;

  /** Must be called from a user gesture on iOS/Chrome. */
  init(): void {
    if (this.ctx) {
      if (this.ctx.state === 'suspended') void this.ctx.resume();
      return;
    }
    const Ctx = window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
    if (!Ctx) return;
    this.ctx = new Ctx();
    this.master = this.ctx.createGain();
    this.master.gain.value = 0.9;
    this.master.connect(this.ctx.destination);
    this.sfxBus = this.ctx.createGain();
    this.sfxBus.gain.value = this.sfxEnabled ? 1 : 0;
    this.sfxBus.connect(this.master);
    this.musicBus = this.ctx.createGain();
    this.musicBus.gain.value = this.musicEnabled ? 0.55 : 0;
    this.musicBus.connect(this.master);
    const len = this.ctx.sampleRate * 1.5;
    this.noiseBuffer = this.ctx.createBuffer(1, len, this.ctx.sampleRate);
    const data = this.noiseBuffer.getChannelData(0);
    for (let i = 0; i < len; i++) data[i] = Math.random() * 2 - 1;
  }

  setSfx(on: boolean): void {
    this.sfxEnabled = on;
    if (this.sfxBus && this.ctx) this.sfxBus.gain.setTargetAtTime(on ? 1 : 0, this.ctx.currentTime, 0.02);
  }

  setMusic(on: boolean): void {
    this.musicEnabled = on;
    if (this.musicBus && this.ctx) this.musicBus.gain.setTargetAtTime(on ? 0.55 : 0, this.ctx.currentTime, 0.1);
  }

  // ------------------------------------------------------------------ SFX

  play(name: SfxName, gain = 1): void {
    if (!this.ctx || !this.sfxBus || !this.sfxEnabled) return;
    const now = this.ctx.currentTime;
    const min = MIN_INTERVAL[name];
    if (min !== undefined) {
      const last = this.lastPlayed[name] ?? -1;
      if (now - last < min) return;
    }
    this.lastPlayed[name] = now;
    const c = this.ctx;
    const out = this.sfxBus;
    switch (name) {
      case 'click':
        this.tone(c, out, 'square', 880, 660, 0.05, 0.12 * gain);
        break;
      case 'pickup':
        this.tone(c, out, 'sine', 420, 640, 0.09, 0.14 * gain);
        break;
      case 'drop':
        this.tone(c, out, 'sine', 500, 320, 0.09, 0.12 * gain);
        break;
      case 'summon':
        this.tone(c, out, 'sine', 300, 720, 0.22, 0.22 * gain);
        this.noise(c, out, 0.12, 0.08 * gain, 1800, 'highpass');
        break;
      case 'merge': {
        const base = 440;
        [0, 0.07, 0.14].forEach((d, i) => this.tone(c, out, 'triangle', base * [1, 1.25, 1.5][i], base * [1, 1.25, 1.5][i], 0.25, 0.2 * gain, now + d));
        this.noise(c, out, 0.25, 0.06 * gain, 3000, 'highpass', now + 0.1);
        break;
      }
      case 'lane':
        this.tone(c, out, 'square', 520, 780, 0.06, 0.1 * gain);
        this.tone(c, out, 'square', 780, 780, 0.06, 0.08 * gain, now + 0.06);
        break;
      case 'shot':
        this.noise(c, out, 0.05, 0.05 * gain, 2500, 'bandpass');
        this.tone(c, out, 'sine', 900, 320, 0.07, 0.05 * gain);
        break;
      case 'fireball':
        this.noise(c, out, 0.2, 0.08 * gain, 900, 'lowpass');
        this.tone(c, out, 'sawtooth', 220, 110, 0.18, 0.04 * gain);
        break;
      case 'mortar':
        this.tone(c, out, 'sine', 160, 60, 0.25, 0.14 * gain);
        this.noise(c, out, 0.1, 0.06 * gain, 600, 'lowpass');
        break;
      case 'laser':
        this.tone(c, out, 'sawtooth', 1200, 900, 0.1, 0.035 * gain);
        break;
      case 'hit':
        this.noise(c, out, 0.03, 0.05 * gain, 1500, 'bandpass');
        break;
      case 'explosion':
        this.noise(c, out, 0.4, 0.22 * gain, 1400, 'lowpass', now, 120);
        this.tone(c, out, 'sine', 120, 40, 0.3, 0.16 * gain);
        break;
      case 'death':
        this.tone(c, out, 'sawtooth', 300, 90, 0.16, 0.07 * gain);
        this.noise(c, out, 0.12, 0.05 * gain, 1000, 'lowpass');
        break;
      case 'leak':
        this.tone(c, out, 'sine', 90, 45, 0.5, 0.35 * gain);
        this.tone(c, out, 'square', 440, 330, 0.25, 0.12 * gain, now + 0.05);
        this.tone(c, out, 'square', 330, 220, 0.3, 0.12 * gain, now + 0.25);
        this.noise(c, out, 0.35, 0.2 * gain, 800, 'lowpass', now, 80);
        break;
      case 'leakEnemy':
        this.tone(c, out, 'triangle', 660, 990, 0.16, 0.16 * gain);
        this.tone(c, out, 'triangle', 990, 1320, 0.2, 0.14 * gain, now + 0.12);
        this.noise(c, out, 0.25, 0.1 * gain, 1200, 'lowpass', now, 100);
        break;
      case 'boss':
        this.horn(c, out, 110, 0.9, 0.22 * gain, now);
        this.horn(c, out, 146.8, 0.9, 0.2 * gain, now + 0.55);
        break;
      case 'commander':
        this.tone(c, out, 'sawtooth', 200, 800, 0.35, 0.12 * gain);
        this.tone(c, out, 'sine', 400, 1600, 0.35, 0.12 * gain, now + 0.05);
        this.noise(c, out, 0.4, 0.08 * gain, 4000, 'highpass', now + 0.1);
        break;
      case 'shop':
        this.tone(c, out, 'sine', 1046, 1046, 0.12, 0.14 * gain);
        this.tone(c, out, 'sine', 1568, 1568, 0.2, 0.12 * gain, now + 0.08);
        break;
      case 'heal':
        this.tone(c, out, 'sine', 700, 1100, 0.2, 0.06 * gain);
        break;
      case 'error':
        this.tone(c, out, 'square', 160, 120, 0.16, 0.1 * gain);
        break;
      case 'overtime':
        this.horn(c, out, 98, 0.6, 0.2 * gain, now);
        this.horn(c, out, 98, 0.6, 0.2 * gain, now + 0.5);
        this.horn(c, out, 130.8, 1.2, 0.22 * gain, now + 1.0);
        break;
      case 'win':
        [523.25, 659.25, 783.99, 1046.5].forEach((f, i) => this.tone(c, out, 'triangle', f, f, 0.5, 0.18 * gain, now + i * 0.13));
        this.noise(c, out, 0.6, 0.07 * gain, 5000, 'highpass', now + 0.4);
        break;
      case 'lose':
        [392, 349.23, 311.13, 261.63].forEach((f, i) => this.tone(c, out, 'sawtooth', f, f * 0.98, 0.6, 0.1 * gain, now + i * 0.22));
        break;
    }
  }

  private tone(
    c: AudioContext,
    out: AudioNode,
    type: OscillatorType,
    f0: number,
    f1: number,
    dur: number,
    vol: number,
    start = c.currentTime,
  ): void {
    const osc = c.createOscillator();
    const g = c.createGain();
    osc.type = type;
    osc.frequency.setValueAtTime(f0, start);
    osc.frequency.exponentialRampToValueAtTime(Math.max(20, f1), start + dur);
    g.gain.setValueAtTime(0.0001, start);
    g.gain.exponentialRampToValueAtTime(vol, start + 0.008);
    g.gain.exponentialRampToValueAtTime(0.0001, start + dur);
    osc.connect(g).connect(out);
    osc.start(start);
    osc.stop(start + dur + 0.02);
  }

  private noise(
    c: AudioContext,
    out: AudioNode,
    dur: number,
    vol: number,
    freq: number,
    type: BiquadFilterType,
    start = c.currentTime,
    sweepTo?: number,
  ): void {
    if (!this.noiseBuffer) return;
    const src = c.createBufferSource();
    src.buffer = this.noiseBuffer;
    const f = c.createBiquadFilter();
    f.type = type;
    f.frequency.setValueAtTime(freq, start);
    if (sweepTo) f.frequency.exponentialRampToValueAtTime(sweepTo, start + dur);
    const g = c.createGain();
    g.gain.setValueAtTime(vol, start);
    g.gain.exponentialRampToValueAtTime(0.0001, start + dur);
    src.connect(f).connect(g).connect(out);
    src.start(start);
    src.stop(start + dur + 0.02);
  }

  private horn(c: AudioContext, out: AudioNode, freq: number, dur: number, vol: number, start: number): void {
    for (const detune of [-6, 0, 6]) {
      const osc = c.createOscillator();
      osc.type = 'sawtooth';
      osc.frequency.value = freq;
      osc.detune.value = detune;
      const f = c.createBiquadFilter();
      f.type = 'lowpass';
      f.frequency.setValueAtTime(400, start);
      f.frequency.linearRampToValueAtTime(1400, start + dur * 0.4);
      f.frequency.linearRampToValueAtTime(300, start + dur);
      const g = c.createGain();
      g.gain.setValueAtTime(0.0001, start);
      g.gain.exponentialRampToValueAtTime(vol / 3, start + 0.12);
      g.gain.setValueAtTime(vol / 3, start + dur * 0.6);
      g.gain.exponentialRampToValueAtTime(0.0001, start + dur);
      osc.connect(f).connect(g).connect(out);
      osc.start(start);
      osc.stop(start + dur + 0.05);
    }
  }

  // ---------------------------------------------------------------- music

  startMusic(): void {
    if (!this.ctx || this.musicPlaying) return;
    this.musicPlaying = true;
    this.bar = 0;
    this.nextBarTime = this.ctx.currentTime + 0.1;
    const schedule = (): void => {
      if (!this.ctx || !this.musicPlaying) return;
      while (this.nextBarTime < this.ctx.currentTime + 0.6) {
        this.scheduleBar(this.nextBarTime);
        this.nextBarTime += this.barLength();
        this.bar++;
      }
    };
    schedule();
    this.musicTimer = window.setInterval(schedule, 200);
  }

  stopMusic(): void {
    this.musicPlaying = false;
    if (this.musicTimer !== null) {
      clearInterval(this.musicTimer);
      this.musicTimer = null;
    }
  }

  private barLength(): number {
    const bpm = 96 + this.intensity * 30;
    return (60 / bpm) * 4;
  }

  /** D minor progression: Dm - Bb - F - C (i - VI - III - VII). */
  private static CHORDS: number[][] = [
    [146.83, 174.61, 220.0],
    [116.54, 146.83, 174.61],
    [174.61, 220.0, 261.63],
    [130.81, 164.81, 196.0],
  ];

  private scheduleBar(start: number): void {
    const c = this.ctx!;
    const out = this.musicBus!;
    const len = this.barLength();
    const chord = AudioEngine.CHORDS[this.bar % 4];
    // Pad
    for (const f of chord) {
      for (const detune of [-5, 5]) {
        const osc = c.createOscillator();
        osc.type = 'sawtooth';
        osc.frequency.value = f;
        osc.detune.value = detune;
        const filt = c.createBiquadFilter();
        filt.type = 'lowpass';
        filt.frequency.value = 500 + this.intensity * 500;
        const g = c.createGain();
        g.gain.setValueAtTime(0.0001, start);
        g.gain.linearRampToValueAtTime(0.045, start + len * 0.3);
        g.gain.linearRampToValueAtTime(0.0001, start + len);
        osc.connect(filt).connect(g).connect(out);
        osc.start(start);
        osc.stop(start + len + 0.05);
      }
    }
    // Bass
    const bass = c.createOscillator();
    bass.type = 'triangle';
    bass.frequency.value = chord[0] / 2;
    const bg = c.createGain();
    bg.gain.setValueAtTime(0.0001, start);
    bg.gain.exponentialRampToValueAtTime(0.12, start + 0.02);
    bg.gain.exponentialRampToValueAtTime(0.0001, start + len * 0.9);
    bass.connect(bg).connect(out);
    bass.start(start);
    bass.stop(start + len);
    // Arpeggio (8ths)
    const steps = 8;
    const arpNotes = [chord[0] * 2, chord[1] * 2, chord[2] * 2, chord[1] * 4];
    for (let i = 0; i < steps; i++) {
      if (this.intensity < 0.3 && i % 2 === 1) continue;
      const t = start + (len / steps) * i;
      const f = arpNotes[i % arpNotes.length];
      const osc = c.createOscillator();
      osc.type = 'triangle';
      osc.frequency.value = f;
      const g = c.createGain();
      g.gain.setValueAtTime(0.0001, t);
      g.gain.exponentialRampToValueAtTime(0.05, t + 0.01);
      g.gain.exponentialRampToValueAtTime(0.0001, t + len / steps * 0.9);
      osc.connect(g).connect(out);
      osc.start(t);
      osc.stop(t + len / steps);
    }
    // Kick on beats when intense
    if (this.intensity > 0.2 && this.noiseBuffer) {
      const beats = this.intensity > 0.7 ? [0, 1, 2, 3] : [0, 2];
      for (const b of beats) {
        const t = start + (len / 4) * b;
        const osc = c.createOscillator();
        osc.type = 'sine';
        osc.frequency.setValueAtTime(140, t);
        osc.frequency.exponentialRampToValueAtTime(40, t + 0.12);
        const g = c.createGain();
        g.gain.setValueAtTime(0.0001, t);
        g.gain.exponentialRampToValueAtTime(0.16, t + 0.005);
        g.gain.exponentialRampToValueAtTime(0.0001, t + 0.18);
        osc.connect(g).connect(out);
        osc.start(t);
        osc.stop(t + 0.2);
      }
    }
  }
}

export const audio = new AudioEngine();
